/**
 * Button component.
 * A button communicates what action will occur when the user clicks it.
 * It can contain text, an Icon, or both.
 *
 * @@oui/core Button
 */
import * as React from "react"
import styled from "@oui/system"
import Actionable from "../Actionable"
import * as Dimensions from "@oui/system/dimensions"
import { stringLiteralArray } from "@oui/system/utils"
import {
  Components,
  interpolations as componentInterpolations
} from "@oui/system/components"
import { button as buttonRoles } from "@oui/system/roles"
import { omit } from "ramda"

type Role = typeof buttonRoles[number] | "button"
type ActionableProps = React.ComponentPropsWithRef<typeof Actionable>
type Appearance = keyof Components["Button"]["appearances"]
type Type = typeof types[number]
type DefaultProps = typeof defaultProps
type BaseProps = Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">
type Props = Readonly<
  {
    /**
     * ARIA defines "role" semantics which can be applied to the button.
     */
    role?: Role
  } & BaseProps &
    Dimensions.Props &
    Omit<ActionableProps, "ref" | "children"> &
    DefaultProps
>

const types = stringLiteralArray(["submit", "reset", "button"])
const defaultProps = {
  /**
   * The visual variant that determines the styling applied to the button.
   * @default "default"
   */
  appearance: "default" as Appearance,
  /**
   * The type of the button.
   * @default "button"
   */
  type: "button" as Type
}

// FIXME: Replace <span> with Text component
const stringToSpan = (child: React.ReactNode) =>
  child?.constructor === String ? <span>{child}</span> : child

const { default: dimensionInterpolations } = Dimensions
const interpolations = {
  ...componentInterpolations.Button,
  ...dimensionInterpolations
}

const Button = ({
  children,
  role,
  forwardedRef,
  ...rest
}: Props & { forwardedRef: React.Ref<HTMLButtonElement> }) => (
  <Actionable
    ref={forwardedRef}
    from="button"
    role={role === "button" ? undefined : role}
    {...omit(Object.keys(interpolations), rest)}
  >
    {React.Children.map(children, stringToSpan)}
  </Actionable>
)
Button.defaultProps = defaultProps

const ForwardedButton_ = (
  props: Omit<Props, keyof DefaultProps> & Partial<DefaultProps>,
  ref: React.Ref<HTMLButtonElement>
) => <Button {...props} forwardedRef={ref} />
ForwardedButton_.displayName = "forwarded(Button)"

const ForwardedButton = React.forwardRef(ForwardedButton_)

const StyledButton = styled(ForwardedButton)(
  {
    appearance: "none",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",

    "&[disabled]": {
      pointerEvents: "none"
    }
  },
  ...(Object.values(interpolations) as any[])
)
StyledButton.defaultProps = defaultProps

export { types, Button as _PropsComponent_ }
export default StyledButton
